import {
  createMeidenConnectApiAccessTokenProvider,
  useMeidenConnectApiTokenWarmup,
  type ApiAccessTokenProvider,
} from "@osakabe/auth-service-provider";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { SessionBackedCurrentActorProvider, useCurrentActor } from "./use-current-actor";
import { useAuthSession } from "./auth-session-context";
import type { WebConfig } from "../env";
import { getWebConfig } from "../env";

export type ApiRequest = (path: string, init?: RequestInit) => Promise<Response>;

interface ApiRequestProviderProps {
  readonly children: ReactNode;
  readonly request: ApiRequest;
}

interface RuntimeApiRequestProviderProps {
  readonly children: ReactNode;
}

interface CreateApiRequestOptions {
  readonly fetchImpl?: typeof fetch | undefined;
  readonly getAccessToken?: ApiAccessTokenProvider | undefined;
  readonly actorProfile?: ActorProfileHeaders | undefined;
}

const ApiRequestContext = createContext<ApiRequest | null>(null);
const ACTOR_DISPLAY_NAME_HEADER = "x-refinement-canvas-actor-name";
const ACTOR_AVATAR_IMAGE_URL_HEADER = "x-refinement-canvas-actor-avatar";

interface ActorProfileHeaders {
  readonly displayName: string;
  readonly avatarImageUrl?: string | undefined;
}

export function ApiRequestProvider({ children, request }: ApiRequestProviderProps): JSX.Element {
  return <ApiRequestContext.Provider value={request}>{children}</ApiRequestContext.Provider>;
}

export function useApiRequest(): ApiRequest {
  const request = useContext(ApiRequestContext);
  if (!request) {
    throw new Error("useApiRequest() は ApiRequestProvider 配下で呼び出してください。");
  }
  return request;
}

export function MockApiRequestProvider({ children }: RuntimeApiRequestProviderProps): JSX.Element {
  const config = getWebConfig();
  const request = useMemo(() => createApiRequest(config), [config]);
  return <ApiRequestProvider request={request}>{children}</ApiRequestProvider>;
}

export function AuthenticatedApiRequestProvider({
  children,
}: RuntimeApiRequestProviderProps): JSX.Element {
  // CurrentActor は SessionSnapshot から導出する。SDK Provider の状態には依存しない。
  return (
    <SessionBackedCurrentActorProvider>
      <AuthenticatedApiRequestProviderInner>{children}</AuthenticatedApiRequestProviderInner>
    </SessionBackedCurrentActorProvider>
  );
}

function AuthenticatedApiRequestProviderInner({
  children,
}: RuntimeApiRequestProviderProps): JSX.Element {
  const config = getWebConfig();
  const { user } = useAuthSession();
  const actor = useCurrentActor();
  const serviceOrigin = config.serviceOrigin ?? window.location.origin;
  // アクセストークン取得は package が提供する保存済みトークン優先経路を使う。
  // SDK Provider が main tree に無くても、IndexedDB / refresh 経由で安定して取得できる。
  const getAccessToken = useMemo<ApiAccessTokenProvider>(() => {
    if (config.authServiceOrigin === undefined || user === undefined) {
      return async () => undefined;
    }

    return createMeidenConnectApiAccessTokenProvider({
      authServiceOrigin: config.authServiceOrigin,
      serviceOrigin,
      userId: user.userId,
    });
  }, [config.authServiceOrigin, serviceOrigin, user]);
  useMeidenConnectApiTokenWarmup(getAccessToken);
  const request = useMemo(
    () =>
      createApiRequest(config, {
        actorProfile: {
          displayName: actor.displayName,
          avatarImageUrl: actor.avatarImageUrl,
        },
        getAccessToken,
      }),
    [actor.avatarImageUrl, actor.displayName, config, getAccessToken]
  );
  return <ApiRequestProvider request={request}>{children}</ApiRequestProvider>;
}

export function createApiRequest(
  config: Pick<WebConfig, "apiBaseUrl" | "authMode">,
  options: CreateApiRequestOptions = {}
): ApiRequest {
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = config.apiBaseUrl.replace(/\/$/, "");

  return async (path, init) => {
    const headers = buildApiHeaders(init?.headers);
    if (config.authMode === "required") {
      if (!options.getAccessToken) {
        throw new Error("authMode=required では getAccessToken が必要です。");
      }
      const accessToken =
        (await options.getAccessToken({ forceRefresh: false })) ??
        (await options.getAccessToken({ forceRefresh: true }));
      if (accessToken === undefined) {
        throw new Error("MEIDEN CONNECT のアクセストークンを取得できませんでした。");
      }
      headers.set("authorization", `Bearer ${accessToken}`);
      applyActorProfileHeaders(headers, options.actorProfile);
    }

    // API 呼び出しは必ず同じ認証情報を伴うよう、credentials を include に固定する。
    const response = await fetchImpl(`${baseUrl}${path}`, {
      ...init,
      credentials: "include",
      headers,
    });
    if (
      response.status === 401 &&
      config.authMode === "required" &&
      options.getAccessToken !== undefined &&
      isIdempotentMethod(init?.method)
    ) {
      const refreshedAccessToken = await options.getAccessToken({ forceRefresh: true });
      if (refreshedAccessToken !== undefined) {
        const retryHeaders = new Headers(headers);
        retryHeaders.set("authorization", `Bearer ${refreshedAccessToken}`);
        return fetchImpl(`${baseUrl}${path}`, {
          ...init,
          credentials: "include",
          headers: retryHeaders,
        });
      }
    }

    return response;
  };
}

function applyActorProfileHeaders(
  headers: Headers,
  profile: ActorProfileHeaders | undefined
): void {
  const displayName = profile?.displayName.trim();
  if (displayName) {
    // Fetch Headers は非 ASCII 文字を直接扱えないため、表示名だけ URI エンコードする。
    headers.set(ACTOR_DISPLAY_NAME_HEADER, encodeURIComponent(displayName));
  }
  const avatarImageUrl = profile?.avatarImageUrl?.trim();
  if (avatarImageUrl) {
    headers.set(ACTOR_AVATAR_IMAGE_URL_HEADER, avatarImageUrl);
  }
}

function buildApiHeaders(extra?: HeadersInit): Headers {
  const headers = new Headers(extra);
  if (!headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  return headers;
}

function isIdempotentMethod(method: string | undefined): boolean {
  const normalizedMethod = (method ?? "GET").toUpperCase();
  return normalizedMethod === "GET" || normalizedMethod === "HEAD";
}
